import {
  AssetFileDeviceModel,
  RegisteredAssetDetailModel,
  useGetAssetQuery,
} from '@casops/store/apis/registeredAssetsApi';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { AssetDeviceType } from '@casops/store/apis/registeredAssetsApi';
import { useGetConfigurationGroupName } from '@casops/hooks/useGetConfigurationGroupName';
import InformationSection from '@casops/components/info-section/InformationSection';
import { GridControlled } from '@casops/components/tables/grid/GridControlled';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useCallback } from 'react';
import { AccordionStatus } from '@dmc/ui';
import {
  GeofenceData,
  useAssetGeofenceTableData,
} from '@casops/hooks/assets/useAssetGeofenceTableData';
import SingleAssetSubPage, {
  HeaderItem,
} from '@casops/templates/single-asset-sub-page/SingleAssetSubPage';
import { AssetNameData, useAssetNameTableData } from '@casops/hooks/assets/useAssetNameTableData';
import {
  columnDefAssetFilesApplied,
  columnDefAssetFilesStatus,
  columnDefAssetNamesName,
  columnDefAssetNamesSource,
  lockColumn,
} from '@casops/components/tables/grid/columnDefs';
import { ColDef, RowClassParams, RowClassRules } from 'ag-grid-community';
import { ConnectivitySnackbar } from '@dmc/ui';
import { ModelName } from '@casops/store/api';
import { EmptyCell } from '@casops/components/tables/formatters/genericFormatters';
import useAssetDeviceFilesColumnDefs from './useAssetDeviceFilesColumnDefs';
import CreateItemButton from '@casops/components/inputs/create-item-button/CreateItemButton';
import { Permission } from '@casops/permissions';
import { SidePanel } from '@casops/store/slices/globalSlice';
import {
  useAbsoluteTimePartialColDef,
  useAbsoluteTimeValueFormatter,
} from '@casops/components/tables/cells/absolute-time/useAbsoluteTime';

const GridSliceKeyFiles = 'AssetFiles';
const GridSliceKeyDeviceFiles = 'AssetDeviceFiles';
const GridSliceKeyGeofences = 'AssetGeofences';
const GridSliceKeyNameMismatch = 'AssetNameMismatch';
const permission = Permission.Admin;
const modelName = ModelName.AssetDeviceFile;
const sidePanel = SidePanel.DeviceFileUpload;

const getRowIdAssetFilesDevices = (data: AssetFileDeviceModel) => String(data.id);
const getRowIdGeofences = (data: GeofenceData) => data.where;
const getRowIdAssetName = (data: AssetNameData) => data.name;

const filesRowClassRules: RowClassRules<AssetFileDeviceModel> = {
  'dm-bold-row': (params: RowClassParams<AssetFileDeviceModel>) => !params.data?.app,
};

const geofenceRowClassRules: RowClassRules<GeofenceData> = {
  'dm-bold-row': (params: RowClassParams<GeofenceData>) => Boolean(params.data?.error),
};

export function AssetConfiguration() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: asset, requestRemainder } = useOutletContext<{
    data: RegisteredAssetDetailModel | undefined;
    requestRemainder: Omit<ReturnType<typeof useGetAssetQuery>, 'data'>;
  }>();
  const fetchError = requestRemainder.error;
  const configGroupName = useGetConfigurationGroupName(asset?.cfg);

  useEffect(() => {
    if (asset?.hw === AssetDeviceType.PPU) {
      // PPUs don't have configuration or diagnostics, so switch to the appropriate tab
      navigate(`/asset/${asset.id}/details`);
    }
  }, [asset?.hw, asset?.id, navigate]);

  const onConfigGroupNameClick = useCallback(() => {
    if (asset?.cfg) {
      navigate(`/configuration/configuration-group/${asset.cfg}/packages`);
    }
  }, [asset?.cfg, navigate]);

  const headerItems = useMemo<HeaderItem[]>(
    () => [
      {
        titleTransId: 'Model.Name.ConfigurationGroup',
        value: configGroupName,
        onClick: onConfigGroupNameClick,
      },
      { titleTransId: 'DeviceSoftware', value: asset?.cas ?? EmptyCell },
      { titleTransId: 'DeviceOs', value: asset?.osVer ?? EmptyCell },
      { titleTransId: 'Updated', value: asset?.tsCfg, isTime: true },
    ],
    [configGroupName, asset?.cas, asset?.osVer, asset?.tsCfg, onConfigGroupNameClick],
  );

  const absoluteTimeColDef = useAbsoluteTimePartialColDef<AssetFileDeviceModel>(EmptyCell);

  const columnDefsPackages: ColDef<AssetFileDeviceModel>[] = useMemo(
    () => [
      { field: 'name', headerName: t('Fields.PackageName') },
      { field: 'ver', headerName: t('Fields.Version') },
      { field: 'rep', headerName: t('Fields.ReportTime'), ...absoluteTimeColDef },
      { field: 'at', headerName: t('Fields.ActivationTime'), ...absoluteTimeColDef },
      columnDefAssetFilesApplied(t),
      { ...columnDefAssetFilesStatus(t), suppressSizeToFit: true },
    ],
    [t, absoluteTimeColDef],
  );

  const columnDefsDeviceFiles = useAssetDeviceFilesColumnDefs();

  const geofenceData = useAssetGeofenceTableData({ asset });
  const absoluteTimeValueFormatter = useAbsoluteTimeValueFormatter<GeofenceData>(EmptyCell);

  const columnDefsGeofences = useMemo<ColDef<GeofenceData>[]>(
    () => [
      { field: 'where', headerName: t('GeofenceInstalled') },
      { field: 'version', headerName: t('Fields.Version') },
      {
        // No quick filter needed (not searchable)
        field: 'ts',
        headerName: t('Updated'),
        valueFormatter: absoluteTimeValueFormatter,
      },
    ],
    [t, absoluteTimeValueFormatter],
  );

  const assetNameData = useAssetNameTableData({ asset });

  const columnDefsAssetNames: ColDef<AssetNameData>[] = useMemo(
    () => [columnDefAssetNamesName(t), columnDefAssetNamesSource(t)],
    [t],
  );

  const statusGeofenceGood = asset && asset.siteGeoVer === asset.geoVer;

  return (
    <>
      <SingleAssetSubPage headerItems={headerItems}>
        <InformationSection
          status={asset?.stC ? AccordionStatus.Healthy : AccordionStatus.Danger}
          icon={asset?.stC ? 'good' : 'caution'}
          title={t('Fields.SoftwareStatus')}
        >
          <GridControlled<AssetFileDeviceModel>
            sliceKey={GridSliceKeyFiles}
            rowData={asset?.cfgSts ?? []}
            columnDefs={columnDefsPackages}
            getRowId={getRowIdAssetFilesDevices}
            rowClassRules={filesRowClassRules}
            noRowsMessage={t('No files available')}
            autoheight
          />
        </InformationSection>

        <InformationSection
          status={statusGeofenceGood ? AccordionStatus.Healthy : AccordionStatus.Danger}
          icon={statusGeofenceGood ? 'good' : 'information'}
          title={t('Fields.GeofenceStatus')}
        >
          <GridControlled<GeofenceData>
            sliceKey={GridSliceKeyGeofences}
            rowData={geofenceData}
            columnDefs={columnDefsGeofences}
            defaultColDef={lockColumn}
            getRowId={getRowIdGeofences}
            rowClassRules={geofenceRowClassRules}
            autoheight
          />
        </InformationSection>

        {!asset?.stN && (
          <InformationSection
            status={AccordionStatus.Danger}
            icon="information"
            title={t('AssetNameMismatch')}
          >
            <GridControlled<AssetNameData>
              sliceKey={GridSliceKeyNameMismatch}
              rowData={assetNameData}
              columnDefs={columnDefsAssetNames}
              defaultColDef={lockColumn}
              getRowId={getRowIdAssetName}
              autoheight
            />
          </InformationSection>
        )}

        <InformationSection
          status={AccordionStatus.Informative}
          icon="folder-closed"
          title={t('Fields.Files')}
          isPadded={false}
          actions={
            <CreateItemButton
              modelName={modelName}
              sidePanel={sidePanel}
              permission={permission}
              label={t('Upload')}
              icon="upload"
              variant="secondary"
            />
          }
        >
          <GridControlled<AssetFileDeviceModel>
            sliceKey={GridSliceKeyDeviceFiles}
            rowData={asset?.files ?? []}
            columnDefs={columnDefsDeviceFiles}
            getRowId={getRowIdAssetFilesDevices}
            rowClassRules={filesRowClassRules}
            noRowsMessage={t('No files available')}
            autoheight
          />
        </InformationSection>
      </SingleAssetSubPage>
      <ConnectivitySnackbar
        error={fetchError}
        message={t('Model.Fetch.Error', { modelName: ModelName.AssetConfiguration })}
        title={t('Error')}
      />
    </>
  );
}

export default AssetConfiguration;
